'use client';

import React, { useEffect } from 'react';
import TitleBar from '../layout/TitleBar';
import { useParams } from 'react-router-dom';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import GenericFormModal from '../form/GenericFormModal';
import { Button, Card, Grid, Typography, Switch, FormControlLabel } from '@mui/material';
import { goaGameSelectList, goaPayoutLevelsSelectList } from '@/stack-shared/constants/goa-constants';
import { useGoaProfitRolling, useGoaSettings, useUpdateGoaSettings, useGoas } from '@/hooks/api/useGoas';
import { useLocations } from '@/hooks/useLocation';
import { useOutages } from '@/hooks/api/useOutages';
import { IGoaSettings } from '@/stack-shared/interfaces/model/goa-settings.interface';
import Loading from '@/components/core/Loading';
import { MaterialReactTable } from 'material-react-table';
import { formatDateHourMinuteSecond } from '@/stack-shared/utils/gen-utils';
import { orderBy } from 'lodash';
import GenericResourceTable from '../form/GenericResourceTable';
import dayjs, { Dayjs } from 'dayjs';
import LineGraph from '../graph/LineGraph';
import ReportOutageActionButton from '@/components/outage/ReportOutageActionButton';
import OutageTable from '@/components/outage/OutageTable';
import { useAuthData } from '@/hooks/getAuthData';

const GoaPage: React.FC = () => {
  const { goaId } = useParams();
  const { data: locationsData } = useLocations();
  const { data: goaSettingsData } = useGoaSettings();
  const { data: outagesData } = useOutages();
  const { data: goasData } = useGoas();
  const updateGoaSettingsMutation = useUpdateGoaSettings();
  
  const locations = Array.isArray(locationsData) ? locationsData : (locationsData ? Object.values(locationsData) : []);
  const goaSettingsRaw = Array.isArray(goaSettingsData) 
    ? goaSettingsData.filter((x: IGoaSettings) => x.locationId === x.locationId)
    : (goaSettingsData ? Object.values(goaSettingsData).filter((x: IGoaSettings) => x.locationId === x.locationId) : []);
  const goaOutages = Array.isArray(outagesData) ? outagesData : (outagesData ? Object.values(outagesData) : []);
  const goas = Array.isArray(goasData) ? goasData : (goasData ? Object.values(goasData) : []);
  const goa = goas.find((x: any) => x.id === parseInt(goaId!));
  const [rollingDailyProfit, setRollingDailyProfit] = React.useState<any>([]);
  let goaLocation = locations.find((y: any) => y.id === goa?.locationId);
  
  // Get current GOA active status for the form
  const currentGoaActive = goa?.active ?? true;
  
  // Check if user is admin
  const { roles } = useAuthData();
  const isAdmin = roles.some((x) => x.name === 'Admin');
  
  let goaSettings = goaSettingsRaw.map((x: IGoaSettings) => {
    return {
      ...x,
      location: locations.find((y: any) => y.id === x.locationId)?.name
    }
  });
  goaSettings = orderBy(goaSettings, 'createdAt', 'desc')

  const initialUpsertSettingsValues = {
    ...goaSettings?.[0],
    active: currentGoaActive,
    assetId: goa?.assetId || '',
    // Preselect the current GOA's location when opening the settings form
    ...(goa?.locationId != null && { locationId: goa.locationId }),
  }

  const { data: profitRollingData } = useGoaProfitRolling(goaId ? { goaId } : undefined);

  useEffect(() => {
    if (profitRollingData?.results) {
      const results = orderBy(profitRollingData.results, 'date', 'asc');
      setRollingDailyProfit(results);
    }
  }, [profitRollingData]);

  if (!goaSettings) {
    return <Loading loading={true} noBackdrop />;
  }

  const [updateGoaSettingsVisible, setUpdateGoaSettingsVisible] = React.useState(false);


  const prepareChartData = (data: {
    date: string;
    netGainPerDay: number;
  }[]) => ({
    labels: data.map((item) => dayjs(item.date).format('MM/DD/YYYY')),
    datasets: [
      {
        label: 'Net Gain Per Day',
        data: data.map((item) => item.netGainPerDay),
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.2)',
        tension: 0.2,
        fill: true,
      },
    ],
  });

  const renderRollingAverageGraph = () => {
    if (rollingDailyProfit){
      const chartData = prepareChartData(rollingDailyProfit); // `results` is the output array from earlier
      return (
        <div style={{ height: '400px', width: '100%' }}>
          <LineGraph
            chartData={chartData}
            uuid="netGainPerDayGraph"
            title="Net Gain Per Day Over Time"
            height={400}
            width={600}
            isCurrency={true}
          />
        </div>
      );
    }
    else{
      return null;
    }

  };

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'GOAs', href: '/app/goas' }, // No href for the current page
    { label: goaId ? goaId.toString() : '', href: '#' }
  ];

  const rightContent = (
    <>
      <ReportOutageActionButton type={'goa'} assetId={goaId!} />
      <Button onClick={() => setUpdateGoaSettingsVisible(true)} variant="contained" sx={{ textTransform: 'none' }}>
        Update GOA Settings
      </Button>
    </>
  );



  const upsertGoaSettings = [{
    groupName: 'Goa Settings',
    fields: [
      {
        label: 'Asset ID', name: 'assetId', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6,
        disabled: !isAdmin
      },
      {
        label: 'Game', name: 'game', type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT, required: false, columns: 6,
        options: goaGameSelectList
      },
      { label: "Max Bet", name: "maxBet", type: GENERIC_FORM_FIELD_TYPE.NUMBER, required: false, columns: 6 },
      {
        label: "Payout Level", name: "payoutLevel", type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT, required: false, columns: 6,
        options: goaPayoutLevelsSelectList
      },
      { label: "Mini", name: "mini", type: GENERIC_FORM_FIELD_TYPE.NUMBER, required: false, columns: 6 },
      { label: "Minor", name: "minor", type: GENERIC_FORM_FIELD_TYPE.NUMBER, required: false, columns: 6 },
      { label: "Major", name: "major", type: GENERIC_FORM_FIELD_TYPE.NUMBER, required: false, columns: 6 },
      { label: "Grand", name: "grand", type: GENERIC_FORM_FIELD_TYPE.NUMBER, required: false, columns: 6 },
      {
        label: "Location", name: "locationId", type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT, required: true, columns: 6,
        options: locations.map((x: any) => {
          return { label: x.name, value: x.id }
        }
        )
      },
      {
        label: "Active Status", name: "active", type: GENERIC_FORM_FIELD_TYPE.SWITCH, required: false, columns: 6,
        defaultValue: currentGoaActive
      },
    ],
  }];

  const goaSettingsColumns = [
    {
      accessorKey: 'createdAt', header: 'Date',
      Cell: ({ row }: any) => {
        return formatDateHourMinuteSecond(row?.original?.date);
      },
    },
    {
      accessorKey: 'game',
      header: 'Game',
    },
    {
      accessorKey: 'maxBet',
      header: 'Max Bet',
    },
    {
      accessorKey: 'payoutLevel',
      header: 'Payout Level',
    },
    {
      accessorKey: 'mini',
      header: 'Mini',
    },
    {
      accessorKey: 'minor',
      header: 'Minor',
    },
    {
      accessorKey: 'major',
      header: 'Major',
    },
    {
      accessorKey: 'grand',
      header: 'Grand',
    },
    {
      accessorKey: 'location',
      header: 'Location',
    },
    {
      accessorKey: 'active',
      header: 'Active Status',
      Cell: ({ row }: any) => {
        return row?.original?.active ? 'Active' : 'Inactive';
      },
    }
  ]

  return (

    <>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title={"GOA: " + goa?.assetId.toString() + ' | ' + goaLocation?.name}
        rightContent={rightContent}
      />

      {goaSettings.length > 0 && (
        <Card sx={{ p: 2, marginBottom: 3 }}>
          <Typography variant="h6" gutterBottom>Current GOA Settings</Typography>
          <Grid container spacing={2}>
            <Grid size={{xs: 6}} ><strong>Game:</strong> {goaSettings[0].game}</Grid>
            <Grid size={{xs: 6}} ><strong>Max Bet:</strong> {goaSettings[0].maxBet}</Grid>
            <Grid size={{xs: 6}} ><strong>Payout Level:</strong> {goaSettings[0].payoutLevel}</Grid>
            <Grid size={{xs: 6}} ><strong>Mini:</strong> {goaSettings[0].mini}</Grid>
            <Grid size={{xs: 6}} ><strong>Minor:</strong> {goaSettings[0].minor}</Grid>
            <Grid size={{xs: 6}} ><strong>Major:</strong> {goaSettings[0].major}</Grid>
            <Grid size={{xs: 6}} ><strong>Grand:</strong> {goaSettings[0].grand}</Grid>
            <Grid size={{xs: 6}} ><strong>Location:</strong> {goaSettings[0].location}</Grid>
            <Grid size={{xs: 6}} ><strong>Active Status:</strong> {goa?.active ? 'Active' : 'Inactive'}</Grid>
          </Grid>
        </Card>
      )}

      {renderRollingAverageGraph()}

      <GenericFormModal
        visible={updateGoaSettingsVisible}
        onDismiss={() => setUpdateGoaSettingsVisible(false)}
        onSubmit={async (values: any) => {
          let submitValues = { ...values }
          delete submitValues.location;
          delete submitValues.createdAt;
          delete submitValues.updatedAt;
          
          // Extract active status and assetId for GOA update
          const { active, assetId, ...goaSettingsValues } = submitValues;
          
          updateGoaSettingsMutation.mutate({ 
            ...goaSettingsValues, 
            goaId,
            active, // Pass active status to backend
            assetId // Pass assetId to backend (only admins can change it)
          }, {
            onSuccess: () => {
              setUpdateGoaSettingsVisible(false);
            },
          });
        }}
        initialValues={initialUpsertSettingsValues}
        fieldGroups={upsertGoaSettings}
        title={'Goa Settings'}
      />
      <MaterialReactTable
        renderTopToolbarCustomActions={() => (
          <Typography variant="h6" >
            GOA Setting History
          </Typography>
        )}
        columns={goaSettingsColumns as any} data={goaSettings} initialState={{ density: 'compact' }} />
        <br/>
      <GenericResourceTable model="goa" modelId={parseInt(goaId!)} />
      <br/>
      <OutageTable type={'goa'} assetId={Number(goaId)} outages={goaOutages} />
    </>

  );
};

export default GoaPage;
